import { ReactElement, useState } from 'react'
import { FilterAlt, FilterAltOutlined } from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { storeyToLabel } from '../utils/storeys'

interface StoreyFilterButtonProps {
  /** Available storeys, sorted. */
  storeys: string[]
  /** Selected storeys; empty means "all". */
  selected: string[]
  onChange: (next: string[]) => void
}

/** Filter the element list by storey — an icon button opening a multi-select storey popover. */
const StoreyFilterButton = ({
  storeys,
  selected,
  onChange,
}: StoreyFilterButtonProps): ReactElement => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const isActive = selected.length > 0

  return (
    <>
      <Tooltip title="Nach Geschoss filtern" arrow disableInteractive>
        <Badge color="primary" variant="dot" invisible={!isActive} overlap="circular">
          <Box
            component="button"
            type="button"
            data-cy="element-list-storey-filter-button"
            onClick={event => setAnchorEl(event.currentTarget)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              flexShrink: 0,
              cursor: 'pointer',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              background: isActive
                ? alpha(theme.palette.primary.main, 0.1)
                : theme.palette.common.white,
              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
              '&:hover': { background: theme.palette.action.hover },
            }}
          >
            {isActive ? (
              <FilterAlt sx={{ fontSize: 18 }} />
            ) : (
              <FilterAltOutlined sx={{ fontSize: 18 }} />
            )}
          </Box>
        </Badge>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 240, p: 1.5 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Geschoss</Typography>
            <Button
              size="small"
              onClick={() => {
                onChange([])
                setAnchorEl(null)
              }}
              disabled={!isActive}
              data-cy="element-list-storey-filter-reset"
              sx={{ fontSize: 11, minWidth: 0, px: 0.75, py: 0.25, textTransform: 'none' }}
            >
              Zurücksetzen
            </Button>
          </Box>

          <ToggleButtonGroup
            size="small"
            value={selected}
            onChange={(_event, next: string[]) => onChange(next)}
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            {storeys.map(storey => (
              <ToggleButton
                key={storey}
                value={storey}
                data-cy={`element-list-storey-option-${storey}`}
                sx={{ px: 1, py: 0.25, fontSize: 12, border: `1px solid ${theme.palette.divider}` }}
              >
                {storeyToLabel(storey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Popover>
    </>
  )
}

export default StoreyFilterButton
